import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Leaf, RefreshCcw, Handshake, ArrowRight,
  Factory, GraduationCap, Sparkles,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import Eyebrow from "@/components/Eyebrow";
import SystemLoop from "@/components/SystemLoop";

const HERO_SLIDES = [
  {
    eyebrow: "Organic Mushrooms",
    headline: ["More than", "a mushroom company."],
    sub: "Organic mushrooms grown on sustainable kenaf-based substrate. From spore to soil, every part of the system gives back more than it takes.",
    img: "/images/cendawan-sisir-hero.jpg",
  },
  {
    eyebrow: "Sustainable Material",
    headline: ["Grown from kenaf.", "Returned to earth."],
    sub: "Kenaf-based beglog. Spent substrate back to compost. Mycelium into packaging. Nothing exits the loop.",
    img: "https://images.unsplash.com/photo-1755606045126-1aeb4f0e7c2d?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
  },
  {
    eyebrow: "Circular Agriculture",
    headline: ["A system built", "to give back."],
    sub: "Ecospora is a circular operating model. Supply, community, commerce, soil, and material all turn together.",
    img: "https://images.unsplash.com/photo-1763844599737-be3850c60cea?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
  },
];

const PARTNERS = [
  { name: "UPM", logo: "/images/partners/upm.png" },
  { name: "LKTN", logo: "/images/partners/lktn.png" },
  { name: "Jabatan Pertanian", logo: "/images/partners/jabatan-pertanian.png" },
  { name: "Letus Brandworks", logo: "/images/partners/letus.png" },
  { name: "RISDA", logo: "/images/partners/risda.png" },
  { name: "FAMA", logo: "/images/partners/fama.png" },
];
const tickerItems = Array.from({ length: 8 }).flatMap(() => PARTNERS);

const PLACEHOLDER_METRICS = [
  { value: "20,000", label: "Monthly beglog" },
  { value: "3", label: "School programmes" },
  { value: "1,000+", label: "Lives touched" },
  { value: "5", label: "Institutional collabs" },
];

const Home = () => {
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    const t = setInterval(
      () => setActiveSlide((s) => (s + 1) % HERO_SLIDES.length),
      5000
    );
    return () => clearInterval(t);
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO CAROUSEL */}
      <section className="relative min-h-screen flex items-end overflow-hidden" data-testid="hero-section">
        {HERO_SLIDES.map((slide, i) => (
          <div
            key={i}
            className={`absolute inset-0 transition-opacity duration-1000 ${i === activeSlide ? "opacity-100" : "opacity-0"}`}
            aria-hidden={i !== activeSlide}
          >
            <img
              src={slide.img}
              alt={slide.eyebrow}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/30 mix-blend-multiply" />
          </div>
        ))}

        <div className="relative container-x pb-24 md:pb-32 pt-32 w-full">
          <div className="max-w-3xl">
            <Eyebrow color="ochre" className="mb-8">
              <span className="text-ecospora-ochre">
                Ecospora · {HERO_SLIDES[activeSlide].eyebrow}
              </span>
            </Eyebrow>
            <h1
              key={activeSlide}
              className="font-display text-5xl md:text-6xl lg:text-[80px] font-bold leading-[1.02] tracking-tight text-ecospora-cream text-balance animate-fade-in"
            >
              {HERO_SLIDES[activeSlide].headline[0]}<br />
              <span className="italic font-light text-ecospora-green-soft">
                {HERO_SLIDES[activeSlide].headline[1]}
              </span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-ecospora-cream/85 max-w-2xl leading-relaxed">
              {HERO_SLIDES[activeSlide].sub}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/impact" className="btn-ochre" data-testid="hero-cta-impact">
                Explore our impact →
              </Link>
              <Link
                to="/collaborate"
                className="inline-flex items-center justify-center gap-2 border border-ecospora-cream/40 text-ecospora-cream uppercase text-sm font-semibold tracking-[0.08em] px-8 py-4 rounded-full hover:bg-ecospora-cream hover:text-ecospora-green-deep transition"
                data-testid="hero-cta-collaborate"
              >
                Collaborate with us
              </Link>
            </div>
          </div>

          {/* Slide indicators */}
          <div className="flex items-center gap-2 mt-10">
            {HERO_SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveSlide(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={`rounded-full transition-all duration-500 ${
                  i === activeSlide
                    ? "w-8 h-2 bg-ecospora-ochre"
                    : "w-2 h-2 bg-ecospora-cream/40 hover:bg-ecospora-cream/70"
                }`}
              />
            ))}
          </div>
        </div>

        <div className="absolute bottom-6 right-6 md:right-12 text-ecospora-cream/40 text-[10px] tracking-[0.3em] uppercase rotate-90 origin-bottom-right">
          Scroll
        </div>
      </section>

      {/* PARTNER TICKER */}
      <section className="py-10 bg-ecospora-white border-b border-ecospora-green-deep/8 overflow-hidden" data-testid="partner-ticker">
        <div className="mb-4 text-center">
          <span className="text-[10px] font-semibold tracking-[0.22em] uppercase text-ecospora-charcoal/40">
            Institutional Partners &amp; Collaborators
          </span>
        </div>
        <div className="overflow-hidden">
          <div className="ticker-track">
            {tickerItems.map((p, i) =>
              p.logo ? (
                <img
                  key={i}
                  src={p.logo}
                  alt={p.name}
                  className="h-12 md:h-14 w-auto object-contain mx-10 md:mx-16 grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition duration-300 shrink-0"
                />
              ) : (
                <span
                  key={i}
                  className="inline-flex items-center px-3 text-base md:text-lg font-bold uppercase tracking-[0.15em] text-ecospora-green-deep/40 shrink-0"
                >
                  {p.name}
                </span>
              )
            )}
          </div>
        </div>
      </section>

      {/* VALUES: GROW / REUSE / EMPOWER */}
      <section className="section-y bg-ecospora-cream" data-testid="values-section">
        <div className="container-x">
          <Reveal>
            <div className="grid md:grid-cols-12 gap-12 items-start">
              <div className="md:col-span-5">
                <Eyebrow className="mb-6">Why We Exist</Eyebrow>
                <h2 className="font-display text-4xl md:text-5xl lg:text-[56px] font-bold tracking-tight leading-[1.05] text-ecospora-green-deep">
                  A platform,{" "}
                  <span className="italic font-light">grown from spores.</span>
                </h2>
              </div>
              <div className="md:col-span-7 md:pt-4">
                <p className="text-lg md:text-xl text-ecospora-charcoal/80 leading-[1.7] max-w-2xl">
                  Most see a vegetable. We see a platform. We grow organic mushrooms on kenaf-based beglog. We compost the spent substrate back into the soil with UPM. We're growing mycelium into biodegradable packaging with USM. From a base in{" "}
                  <strong className="text-ecospora-green-deep">Perlis</strong>, the system is going national.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal stagger className="grid md:grid-cols-3 gap-8 md:gap-12 mt-24">
            {[
              {
                icon: Leaf,
                title: "Grow",
                body: "Organic mushrooms cultivated on kenaf-based beglog — a higher-performing, more sustainable substrate.",
              },
              {
                icon: RefreshCcw,
                title: "Reuse",
                body: "Spent substrate becomes compost. Mycelium becomes packaging. Nothing leaves the loop.",
              },
              {
                icon: Handshake,
                title: "Empower",
                body: "At our facility, OKU workers, single mothers, and elderly community members run the production line. Income that counts. Community programmes that reach further.",
              },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} className="group">
                <div className="w-14 h-14 rounded-full bg-ecospora-white border border-ecospora-green-deep/15 flex items-center justify-center mb-6 group-hover:bg-ecospora-green-deep transition-colors">
                  <Icon size={22} strokeWidth={1.5} className="text-ecospora-green-deep group-hover:text-ecospora-cream transition-colors" />
                </div>
                <h3 className="font-display text-3xl font-semibold tracking-tight text-ecospora-green-deep mb-3">
                  {title}
                </h3>
                <p className="text-ecospora-charcoal/75 leading-relaxed">{body}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* STATS */}
      <section className="section-y bg-ecospora-green-soft" data-testid="stats-section">
        <div className="container-x">
          <Reveal className="text-center max-w-3xl mx-auto mb-16">
            <Eyebrow className="justify-center mb-6 mx-auto inline-flex">Proof you can measure.</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-ecospora-green-deep leading-[1.1]">
              The numbers behind the model.
            </h2>
          </Reveal>

          <Reveal stagger className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-6">
            {PLACEHOLDER_METRICS.map((m, i) => (
              <div key={i} className="text-center md:text-left border-t border-ecospora-green-deep/20 pt-6" data-testid={`metric-${i}`}>
                <div className="font-display text-5xl md:text-6xl font-bold text-ecospora-green-deep tracking-tight">
                  {m.value}
                </div>
                <div className="mt-3 text-sm text-ecospora-charcoal/80 leading-snug">{m.label}</div>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* CIRCULAR ECONOMY */}
      <section className="section-y bg-ecospora-cream" data-testid="circular-economy-section">
        <div className="container-x">
          <div className="grid md:grid-cols-12 gap-12 md:gap-16 items-center">
            <Reveal className="md:col-span-6">
              <Eyebrow className="mb-6">Circular Economy</Eyebrow>
              <h2 className="font-display text-4xl md:text-5xl lg:text-[56px] font-bold tracking-tight text-ecospora-green-deep leading-[1.05]">
                Everything we grow{" "}
                <span className="italic font-light">comes back.</span>
              </h2>
              <p className="mt-8 text-lg md:text-xl text-ecospora-charcoal/80 leading-[1.7] max-w-[480px]">
                Kenaf grows the mushroom. Mushroom waste becomes compost. Compost rebuilds the soil. The soil grows the kenaf. Six steps. One unbroken circle.
              </p>
              <Link
                to="/impact"
                className="inline-flex items-center gap-2 mt-10 text-ecospora-green-deep font-semibold border-b border-ecospora-ochre pb-1 hover:gap-3 transition-all"
                data-testid="circular-cta"
              >
                See how it turns <ArrowRight size={18} strokeWidth={1.5} />
              </Link>
            </Reveal>
            <Reveal className="md:col-span-6">
              <SystemLoop />
            </Reveal>
          </div>
        </div>
      </section>

      {/* SOCIAL IMPACT */}
      <section className="section-y bg-ecospora-green-deep" data-testid="social-impact-section">
        <div className="container-x">
          <div className="grid md:grid-cols-12 gap-12 md:gap-16 items-center">
            <Reveal className="md:col-span-7">
              <Eyebrow color="ochre" className="mb-6 text-ecospora-ochre">Social Impact</Eyebrow>
              <h2 className="font-display text-4xl md:text-5xl lg:text-[56px] font-bold tracking-tight text-ecospora-cream leading-[1.05]">
                Employment by design.{" "}
                <span className="italic font-light text-ecospora-green-soft">Built into how we operate.</span>
              </h2>
              <p className="mt-8 text-lg md:text-xl text-ecospora-cream/80 leading-[1.7] max-w-[520px]">
                Ecospora employs OKU workers, single mothers, elderly community members, and asnaf families at every level of production. Our facility in Perlis is built around inclusive employment as a structural part of how we operate, with community programmes that reach beyond the production line. When Ecospora grows, these communities grow with it.
              </p>
              <Link
                to="/impact"
                className="inline-flex items-center gap-2 mt-10 text-ecospora-ochre font-semibold border-b border-ecospora-ochre/50 pb-1 hover:gap-3 transition-all"
                data-testid="social-impact-cta"
              >
                See our impact <ArrowRight size={18} strokeWidth={1.5} />
              </Link>
            </Reveal>
            <Reveal className="md:col-span-5 hidden md:flex items-center justify-center">
              <div className="text-center opacity-20 select-none">
                <div className="font-display text-[160px] font-bold leading-none text-ecospora-cream tracking-tight">
                  +1k
                </div>
                <div className="text-ecospora-cream/60 text-sm tracking-[0.2em] uppercase mt-2">
                  Lives touched
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* OUR BRANDS */}
      <section className="section-y bg-ecospora-white" data-testid="brands-section">
        <div className="container-x">
          <Reveal className="max-w-3xl mb-16">
            <Eyebrow className="mb-6">Our Brands</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-ecospora-green-deep leading-[1.1]">
              Two brands.{" "}
              <span className="italic font-light">Full market coverage.</span>
            </h2>
            <p className="mt-6 text-lg text-ecospora-charcoal/75 max-w-2xl leading-relaxed">
              Every Ecospora-grown mushroom reaches the market through one of our two consumer brands. Different audiences, different price tiers, the same standards.
            </p>
          </Reveal>

          <Reveal stagger className="grid md:grid-cols-2 gap-6 md:gap-8">
            <BrandCard
              tier="Premium tier"
              name="KukurMas"
              body="Specialty mushrooms for chefs, restaurants, and households who taste the difference."
              tone="dark"
              testid="brand-kukurmas"
              img="/images/cendawan-sisir-hero.jpg"
            />
            <BrandCard
              tier="Value tier"
              name="Cendawan Hub"
              body="Everyday mushrooms and mushroom-based foods, accessible to every Malaysian kitchen."
              tone="light"
              testid="brand-cendawan"
              img="https://images.unsplash.com/photo-1763844599737-be3850c60cea?crop=entropy&cs=srgb&fm=jpg&w=800&q=80"
            />
          </Reveal>
        </div>
      </section>

      {/* WHERE DO YOU FIT */}
      <section className="section-y bg-ecospora-cream" data-testid="audience-section">
        <div className="container-x">
          <Reveal className="text-center mb-16">
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-ecospora-green-deep">
              Where do you fit?
            </h2>
          </Reveal>

          <Reveal stagger className="grid md:grid-cols-3 gap-6 md:gap-8">
            <AudienceCard
              icon={Factory}
              title="For Businesses"
              desc="Reliable beglog and bulk mushroom supply for growers, retailers, and food producers."
              cta="See B2B solutions"
              to="/collaborate"
              testid="audience-b2b"
            />
            <AudienceCard
              icon={Handshake}
              title="For Agencies"
              desc="Bring Ecospora and our sub-brands to your market. Defined territories, real margin, full support."
              cta="Explore agency partnership"
              to="/collaborate"
              testid="audience-agencies"
            />
            <AudienceCard
              icon={GraduationCap}
              title="For Universities"
              desc="Research partnerships, joint R&D, and academic collaboration. We bring the system; you bring the lab."
              cta="Explore research collaboration"
              to="/collaborate"
              testid="audience-universities"
            />
          </Reveal>

          <Reveal className="text-center mt-12">
            <p className="text-sm text-ecospora-charcoal/60 italic">
              Looking to buy our mushrooms? Visit{" "}
              <a href="#" className="underline decoration-ecospora-ochre underline-offset-4 text-ecospora-green-deep">KukurMas</a>{" "}
              or{" "}
              <a href="#" className="underline decoration-ecospora-ochre underline-offset-4 text-ecospora-green-deep">Cendawan Hub</a>.
            </p>
          </Reveal>
        </div>
      </section>

      {/* JOURNAL */}
      <section className="section-y bg-ecospora-white" data-testid="journal-section">
        <div className="container-x">
          <Reveal className="flex items-end justify-between mb-16 gap-6">
            <div>
              <Eyebrow className="mb-6">Journal</Eyebrow>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-ecospora-green-deep leading-[1.1]">
                From the field.{" "}
                <span className="italic font-light">From the lab.</span>
              </h2>
            </div>
            <Link
              to="/journal"
              className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-ecospora-green-deep border-b border-ecospora-ochre pb-1 hover:gap-3 transition-all shrink-0"
            >
              All entries <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </Reveal>

          <Reveal stagger className="grid md:grid-cols-3 gap-8">
            {JOURNAL_PREVIEW.map((post) => (
              <JournalPreviewCard key={post.slug} {...post} />
            ))}
          </Reveal>

          <Reveal className="text-center mt-12 md:hidden">
            <Link to="/journal" className="btn-secondary">
              All journal entries
            </Link>
          </Reveal>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="bg-ecospora-green-deep py-24 md:py-32">
        <div className="container-x">
          <Reveal className="text-center max-w-3xl mx-auto">
            <Sparkles size={32} strokeWidth={1.25} className="text-ecospora-ochre mx-auto mb-8" />
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-ecospora-cream leading-[1.1] text-balance">
              Let's grow something that{" "}
              <span className="italic font-light text-ecospora-green-soft">matters.</span>
            </h2>
            <Link to="/contact#form" className="btn-ochre mt-10" data-testid="closing-cta">
              Talk to us →
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

const JOURNAL_PREVIEW = [
  {
    slug: "kenaf-beglog-yield-rates",
    category: "Field Notes",
    date: "March 2025",
    title: "What kenaf-based beglog actually does to yield rates",
    img: "/images/cendawan-sisir-hero.jpg",
  },
  {
    slug: "oku-employment-model",
    category: "Community",
    date: "February 2025",
    title: "Inside our OKU employment model at the Perlis facility",
    img: "https://images.unsplash.com/photo-1755606045126-1aeb4f0e7c2d?crop=entropy&cs=srgb&fm=jpg&w=800&q=80",
  },
  {
    slug: "upm-composting-trial",
    category: "Research",
    date: "January 2025",
    title: "First results from the UPM substrate composting trial",
    img: "https://images.unsplash.com/photo-1763844599737-be3850c60cea?crop=entropy&cs=srgb&fm=jpg&w=800&q=80",
  },
];

const BrandCard = ({ tier, name, body, tone, testid, img }) => {
  const dark = tone === "dark";
  return (
    <div
      data-testid={testid}
      className={`group rounded-2xl border overflow-hidden transition-all duration-500 hover:-translate-y-1 ${
        dark
          ? "bg-ecospora-green-deep text-ecospora-cream border-ecospora-green-deep"
          : "bg-ecospora-cream text-ecospora-charcoal border-ecospora-green-deep/15"
      }`}
    >
      {img && (
        <div className="h-48 overflow-hidden">
          <img
            src={img}
            alt={name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
          />
        </div>
      )}
      <div className="p-10 md:p-14">
        <span className={`text-[11px] font-semibold tracking-[0.22em] uppercase ${dark ? "text-ecospora-ochre" : "text-ecospora-green-mid"}`}>
          {tier}
        </span>
        <h3 className={`font-display text-3xl md:text-4xl font-bold tracking-tight mt-4 ${dark ? "text-ecospora-cream" : "text-ecospora-green-deep"}`}>
          {name}
        </h3>
        <p className={`mt-5 text-lg leading-relaxed ${dark ? "text-ecospora-cream/80" : "text-ecospora-charcoal/75"}`}>
          {body}
        </p>
        <a
          href="#"
          className={`inline-flex items-center gap-2 mt-10 text-sm font-semibold uppercase tracking-[0.08em] ${dark ? "text-ecospora-ochre" : "text-ecospora-green-deep"} border-b ${dark ? "border-ecospora-ochre/50" : "border-ecospora-green-deep/30"} pb-1 group-hover:gap-3 transition-all`}
        >
          Visit {name} <ArrowRight size={16} strokeWidth={1.5} />
        </a>
      </div>
    </div>
  );
};

const AudienceCard = ({ icon: Icon, title, desc, cta, to, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className="group block bg-ecospora-white rounded-2xl p-10 border border-ecospora-green-deep/10 hover:border-ecospora-green-deep/40 hover:-translate-y-1 transition-all duration-500"
  >
    <div className="w-12 h-12 rounded-full bg-ecospora-cream flex items-center justify-center mb-8 group-hover:bg-ecospora-green-deep transition-colors">
      <Icon size={20} strokeWidth={1.5} className="text-ecospora-green-deep group-hover:text-ecospora-cream transition-colors" />
    </div>
    <h3 className="font-display text-2xl font-semibold tracking-tight text-ecospora-green-deep mb-4">
      {title}
    </h3>
    <p className="text-ecospora-charcoal/75 leading-relaxed mb-8">{desc}</p>
    <span className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.08em] text-ecospora-green-deep group-hover:gap-3 transition-all">
      {cta} <ArrowRight size={16} strokeWidth={1.5} />
    </span>
  </Link>
);

const JournalPreviewCard = ({ slug, category, date, title, img }) => (
  <Link
    to={`/journal/${slug}`}
    className="group block rounded-2xl overflow-hidden border border-ecospora-green-deep/10 bg-ecospora-cream hover:-translate-y-1 transition-all duration-500"
    data-testid={`journal-preview-${slug}`}
  >
    <div className="aspect-video overflow-hidden bg-ecospora-green-soft">
      <img
        src={img}
        alt={title}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
      />
    </div>
    <div className="p-8">
      <div className="flex items-center gap-3 mb-4">
        <span className="text-[11px] font-semibold tracking-[0.18em] uppercase text-ecospora-green-mid">
          {category}
        </span>
        <span className="text-ecospora-charcoal/30">·</span>
        <span className="text-xs text-ecospora-charcoal/50">{date}</span>
      </div>
      <h3 className="font-display text-xl font-semibold tracking-tight text-ecospora-green-deep leading-snug mb-4">
        {title}
      </h3>
      <span className="inline-flex items-center gap-2 text-sm font-semibold text-ecospora-green-deep group-hover:gap-3 transition-all">
        Read more <ArrowRight size={15} strokeWidth={1.5} />
      </span>
    </div>
  </Link>
);

export default Home;
